import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ChevronRightIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Input, Select, Card, Badge, Modal, ModalFooter, LoadingSpinner } from '@/components/common';
import api from '@/services/api';
import toast from 'react-hot-toast';

// --- Types ---

interface Channel {
  id: string;
  name: string;
  code: string;
  channel_type: string;
  level?: number;
  parent?: string | null;
  parent_name?: string | null;
  is_active: boolean;
}

interface ChannelTreeNode {
  id: string;
  name: string;
  code: string;
  channel_type: string;
  level: number;
  is_active: boolean;
  children: ChannelTreeNode[];
}

interface PaginatedChannels {
  count: number;
  results: Channel[];
}

// --- Constants ---

// #357 (owner decision): an insurer's distribution channels use insurer
// terminology, not the broker/POSP list. The insurer channel API accepts
// exactly these values (Channel.INSURER_TYPE_CHOICES); the broker side is
// unchanged.
const CHANNEL_TYPE_OPTIONS = [
  { value: 'BROKER', label: 'Broker' },
  { value: 'CORPORATE_AGENT', label: 'Corporate Agent' },
  { value: 'INDIVIDUAL_AGENT', label: 'Individual Agent' },
  { value: 'BANCASSURANCE', label: 'Bancassurance' },
  { value: 'DIRECT', label: 'Direct' },
  { value: 'WEB', label: 'Web' },
  { value: 'WEB_AGGREGATOR', label: 'Web Aggregator' },
  { value: 'IMF', label: 'IMF' },
  { value: 'OTHER', label: 'Other' },
];

// Channels created before #357 may still carry a broker-style type; show them
// readably rather than as a raw code.
const LEGACY_TYPE_LABELS: Record<string, string> = {
  POSP: 'POSP',
  ADVERTISER: 'Advertiser',
  CONSOLIDATOR: 'Consolidator',
  AGENT: 'Agent',
  BRANCH: 'Branch',
  PARTNER: 'Partner',
};

function channelTypeLabel(code: string): string {
  return CHANNEL_TYPE_OPTIONS.find((o) => o.value === code)?.label ?? LEGACY_TYPE_LABELS[code] ?? code;
}

const CHANNEL_TYPE_COLORS: Record<string, string> = {
  BROKER: 'bg-blue-100 text-blue-700',
  CORPORATE_AGENT: 'bg-purple-100 text-purple-700',
  INDIVIDUAL_AGENT: 'bg-green-100 text-green-700',
  BANCASSURANCE: 'bg-orange-100 text-orange-700',
  DIRECT: 'bg-cyan-100 text-cyan-700',
  WEB: 'bg-indigo-100 text-indigo-700',
  WEB_AGGREGATOR: 'bg-pink-100 text-pink-700',
  IMF: 'bg-amber-100 text-amber-700',
  OTHER: 'bg-gray-100 text-gray-700',
};

const INITIAL_FORM = {
  name: '',
  code: '',
  channel_type: 'BROKER',
  parent: '',
};

type FormErrors = Partial<Record<'name' | 'code' | 'channel_type' | 'parent', string>>;

// The API's field errors: {error: {fields: {code: ['...']}}}.
function apiFieldErrors(err: unknown): FormErrors {
  const fields = (err as { response?: { data?: { error?: { fields?: Record<string, unknown> } } } })?.response?.data
    ?.error?.fields;
  const out: FormErrors = {};
  if (!fields) return out;
  for (const key of ['name', 'code', 'channel_type', 'parent'] as const) {
    const v = fields[key];
    if (v) out[key] = Array.isArray(v) ? String(v[0]) : String(v);
  }
  return out;
}

// --- TreeNode Component ---

function TreeNode({ node, depth = 0 }: { node: ChannelTreeNode; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 1);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-2 px-3 hover:bg-gray-50 rounded-lg transition-colors ${
          depth > 0 ? 'ml-' + Math.min(depth * 6, 24) : ''
        }`}
        style={{ marginLeft: depth * 24 }}
      >
        {/* Expand/Collapse toggle. #357: named and stateful for assistive tech. */}
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
            className="p-0.5 rounded hover:bg-gray-200 text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {expanded ? (
              <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
        ) : (
          <span className="w-5" aria-hidden="true" />
        )}

        {/* Channel info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 truncate">{node.name}</span>
              <span className="text-xs text-gray-400">{node.code}</span>
            </div>
          </div>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              CHANNEL_TYPE_COLORS[node.channel_type] ?? CHANNEL_TYPE_COLORS.OTHER
            }`}
          >
            {channelTypeLabel(node.channel_type)}
          </span>
          <Badge variant={node.is_active ? 'success' : 'error'} size="sm">
            {node.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Component ---

export function ChannelsPage() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  // #357: the channel being edited (same modal and form as Add).
  const [editing, setEditing] = useState<Channel | null>(null);
  const formOpen = showAddModal || editing !== null;
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Fetch flat channel list
  const { data: channelsData, isLoading: isLoadingList } = useQuery<PaginatedChannels>({
    queryKey: ['insurance-channels-list', page],
    queryFn: async () => {
      const res = await api.get('/insurers/insurance/channels/', { params: { page } });
      return res.data;
    },
    enabled: viewMode === 'list',
  });

  // Fetch tree hierarchy
  const { data: hierarchy = [], isLoading: isLoadingTree } = useQuery<ChannelTreeNode[]>({
    queryKey: ['insurance-channels-hierarchy'],
    queryFn: async () => {
      const res = await api.get('/insurers/insurance/channels/hierarchy/');
      return res.data;
    },
    enabled: viewMode === 'tree',
  });

  // Fetch all channels for parent dropdown (flat simple list)
  const { data: allChannels = [] } = useQuery<Channel[]>({
    queryKey: ['insurance-channels-all'],
    queryFn: async () => {
      const res = await api.get('/insurers/insurance/channels/', { params: { page_size: 999 } });
      return res.data.results || res.data;
    },
    enabled: formOpen,
  });

  // Create channel
  const createMutation = useMutation({
    mutationFn: async (data: typeof INITIAL_FORM) => {
      const payload: Record<string, string> = {
        name: data.name,
        code: data.code,
        channel_type: data.channel_type,
      };
      if (data.parent) {
        payload.parent = data.parent;
      }
      const res = await api.post('/insurers/insurance/channels/', payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-channels-list'] });
      queryClient.invalidateQueries({ queryKey: ['insurance-channels-hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['insurance-channels-all'] });
      setShowAddModal(false);
      setFormData(INITIAL_FORM);
      setFormErrors({});
      toast.success('Channel created successfully');
    },
    onError: (err) => {
      const fieldErrors = apiFieldErrors(err);
      setFormErrors(fieldErrors);
      toast.error(Object.values(fieldErrors)[0] ?? 'Failed to create channel');
    },
  });

  const invalidateChannels = () => {
    queryClient.invalidateQueries({ queryKey: ['insurance-channels-list'] });
    queryClient.invalidateQueries({ queryKey: ['insurance-channels-hierarchy'] });
    queryClient.invalidateQueries({ queryKey: ['insurance-channels-all'] });
  };

  // Edit: PATCH. A legacy broker-style type is only sent when the admin
  // picked an insurer type (the API refuses the old values).
  const updateMutation = useMutation({
    mutationFn: async ({ id, data, typeChanged }: { id: string; data: typeof INITIAL_FORM; typeChanged: boolean }) => {
      const payload: Record<string, string | null> = {
        name: data.name,
        code: data.code,
        parent: data.parent || null,
      };
      if (typeChanged) payload.channel_type = data.channel_type;
      const res = await api.patch(`/insurers/insurance/channels/${id}/`, payload);
      return res.data;
    },
    onSuccess: () => {
      invalidateChannels();
      setEditing(null);
      setFormData(INITIAL_FORM);
      setFormErrors({});
      toast.success('Channel updated');
    },
    onError: (err) => {
      const fieldErrors = apiFieldErrors(err);
      setFormErrors(fieldErrors);
      toast.error(Object.values(fieldErrors)[0] ?? 'Failed to update channel');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async (channel: Channel) => {
      const res = await api.patch(`/insurers/insurance/channels/${channel.id}/`, { is_active: !channel.is_active });
      return res.data;
    },
    onSuccess: (_data, channel) => {
      invalidateChannels();
      toast.success(channel.is_active ? 'Channel deactivated' : 'Channel reactivated');
    },
    onError: () => toast.error('Could not change the channel status'),
  });

  const channels = channelsData?.results ?? [];
  const totalItems = channelsData?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20);

  const openAddModal = () => {
    setFormData(INITIAL_FORM);
    setFormErrors({});
    setShowAddModal(true);
  };

  const openEditModal = (channel: Channel) => {
    setFormData({
      name: channel.name,
      code: channel.code,
      channel_type: channel.channel_type,
      parent: channel.parent ?? '',
    });
    setFormErrors({});
    setEditing(channel);
  };

  const closeForm = () => {
    setShowAddModal(false);
    setEditing(null);
  };

  // #357: errors are shown on the fields themselves, and focus moves to the
  // first invalid one, instead of a toast a screen reader may never announce.
  const handleCreate = (event: React.FormEvent) => {
    event.preventDefault();
    const errors: FormErrors = {};
    if (!formData.name.trim()) errors.name = 'Channel name is required';
    if (!formData.code.trim()) errors.code = 'Channel code is required';
    setFormErrors(errors);
    const firstInvalid = (['name', 'code'] as const).find((field) => errors[field]);
    if (firstInvalid) {
      document.getElementById(`channel-${firstInvalid}`)?.focus();
      return;
    }
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: formData, typeChanged: formData.channel_type !== editing.channel_type });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = viewMode === 'list' ? isLoadingList : isLoadingTree;

  return (
    <div>
      <PageHeader
        title="Channels"
        subtitle="Manage distribution channels and hierarchy"
        breadcrumbs={[{ label: 'Insurance' }, { label: 'Channels' }]}
        actions={
          <div className="flex gap-2">
            {/* View toggle */}
            <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="View">
              <button
                type="button"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 text-sm flex items-center gap-1.5 ${
                  viewMode === 'list'
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ListBulletIcon className="w-4 h-4" aria-hidden="true" />
                List
              </button>
              <button
                type="button"
                aria-pressed={viewMode === 'tree'}
                onClick={() => setViewMode('tree')}
                className={`px-3 py-2 text-sm flex items-center gap-1.5 border-l border-gray-200 ${
                  viewMode === 'tree'
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Squares2X2Icon className="w-4 h-4" aria-hidden="true" />
                Tree
              </button>
            </div>

            <Button onClick={openAddModal} leftIcon={<PlusIcon className="w-5 h-5" />}>
              Add Channel
            </Button>
          </div>
        }
      />

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : viewMode === 'list' ? (
        /* --- Flat List View --- */
        <>
          <Card padding="none">
            {channels.length === 0 ? (
              <div className="text-center py-12">
                <BuildingOfficeIcon className="w-12 h-12 mx-auto text-gray-400" />
                <h3 className="mt-4 text-sm font-semibold text-gray-900">No channels found</h3>
                <p className="mt-1 text-sm text-gray-500">Create your first distribution channel.</p>
                <Button className="mt-4" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={openAddModal}>
                  Add Channel
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Channel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Parent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {channels.map((channel) => (
                      <tr key={channel.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-900">{channel.name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-500">{channel.code}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              CHANNEL_TYPE_COLORS[channel.channel_type] ?? CHANNEL_TYPE_COLORS.OTHER
                            }`}
                          >
                            {channelTypeLabel(channel.channel_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-500">{channel.parent_name ?? '-'}</p>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={channel.is_active ? 'success' : 'error'} size="sm">
                            {channel.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              leftIcon={<PencilSquareIcon className="w-4 h-4" aria-hidden="true" />}
                              aria-label={`Edit ${channel.name}`}
                              onClick={() => openEditModal(channel)}
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              aria-label={`${channel.is_active ? 'Deactivate' : 'Reactivate'} ${channel.name}`}
                              disabled={toggleActiveMutation.isPending}
                              onClick={() => toggleActiveMutation.mutate(channel)}
                            >
                              {channel.is_active ? 'Deactivate' : 'Reactivate'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Page {page} of {totalPages} ({totalItems} channels)
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </>
      ) : (
        /* --- Tree Hierarchy View --- */
        <Card>
          {hierarchy.length === 0 ? (
            <div className="text-center py-12">
              <BuildingOfficeIcon className="w-12 h-12 mx-auto text-gray-400" />
              <h3 className="mt-4 text-sm font-semibold text-gray-900">No channel hierarchy found</h3>
              <p className="mt-1 text-sm text-gray-500">Create channels to build your hierarchy.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {hierarchy.map((node) => (
                <TreeNode key={node.id} node={node} depth={0} />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Add Channel Modal */}
      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'Edit Channel' : 'Add Channel'}
        size="lg"
      >
        {/* #357: a real <form> (Enter submits), labels tied to their fields by
            id, ONE required marker each (Input/Select draw the "*" when
            `required` is set -- the labels used to carry a second one), and
            field-level errors marked aria-invalid. */}
        <form onSubmit={handleCreate} noValidate aria-label={editing ? 'Edit channel' : 'Add channel'}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                id="channel-name"
                label="Channel Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., North Region Branch"
                required
                aria-invalid={formErrors.name ? true : undefined}
                error={formErrors.name}
              />
              <Input
                id="channel-code"
                label="Channel Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., NRB001"
                required
                aria-invalid={formErrors.code ? true : undefined}
                error={formErrors.code}
              />
            </div>
            <Select
              id="channel-type"
              label="Channel Type"
              options={
                // A legacy row keeps its old label until the admin picks an insurer type.
                editing && !CHANNEL_TYPE_OPTIONS.some((o) => o.value === editing.channel_type)
                  ? [{ value: editing.channel_type, label: channelTypeLabel(editing.channel_type) }, ...CHANNEL_TYPE_OPTIONS]
                  : CHANNEL_TYPE_OPTIONS
              }
              value={formData.channel_type}
              onChange={(e) => setFormData({ ...formData, channel_type: e.target.value })}
              required
              aria-invalid={formErrors.channel_type ? true : undefined}
              error={formErrors.channel_type}
            />
            <Select
              id="channel-parent"
              label="Parent Channel (Optional)"
              options={[
                { value: '', label: 'None (Top-level)' },
                ...allChannels
                  .filter((ch) => ch.id !== editing?.id)
                  .map((ch) => ({ value: ch.id, label: `${ch.name} (${ch.code})` })),
              ]}
              value={formData.parent}
              onChange={(e) => setFormData({ ...formData, parent: e.target.value })}
              aria-invalid={formErrors.parent ? true : undefined}
              error={formErrors.parent}
            />
          </div>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={closeForm}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Save Changes' : 'Create Channel'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}
